import { scaleAvgFillPrice } from '@/features/scale/lib/scale-status'
import { toExchangeEnum, tradeVenueToExchangeKey } from '@/lib/proto/util'
import { safeParseFloat } from '@/lib/utils'
import type { TradeHistoryItem } from '@/state/account'
import { AlgoState, AlgoStatus, AlgoType, ScaleLevelStatus } from '@/types/generated/algo'
import {
  Denomination,
  Order,
  OrderSide,
  OrderStatus,
  OrderType,
  Position,
} from '@/types/generated/common/trading'
import { InstrumentKind } from '@/types/generated/common/types'
import { Exchange, TradeVenue } from '@/types/generated/common/types'
import { UnifiedOrderItem } from '@/types/generated/public_api'

// Pure, account-scoped merge/dedup helpers for orders, trades, and algo states,
// shared by the per-account WS/REST fan-out (account-orders-sync.tsx) and by the
// table components' row IDs and cancel/modify lookups. Every consumer
// MUST derive an order/trade/algo's identity through the composite-key helpers
// below rather than re-deriving an equivalent key locally — a raw orderId,
// venueSymbol, or algoId is only guaranteed unique *within* one account, not
// across the accounts this module merges together.

export const MAX_HISTORY_SIZE = 1000
// Cap, per retained algo type, on terminal (fired/cancelled/failed) algos kept for
// the Triggers and Hedges tab history sections.
export const MAX_TERMINAL_ALGOS = 100

export const TERMINAL_ALGO_STATUSES = new Set([
  AlgoStatus.ALGO_STATUS_COMPLETED,
  AlgoStatus.ALGO_STATUS_CANCELLED,
  AlgoStatus.ALGO_STATUS_FAILED,
])

// Algo types whose terminal (completed / cancelled / failed) states are retained
// instead of filtered out, so their tabs can show a Past / history section. Other
// algo types (e.g. standalone TWAP) are dropped once terminal.
export const RETAIN_TERMINAL_ALGO_TYPES = new Set<AlgoType>([
  AlgoType.ALGO_TYPE_TRIGGER,
  AlgoType.ALGO_TYPE_HEDGE,
  // Retain terminal auto-RFQ algos so the RFQs tab can surface completed auto-RFQs in
  // its Past section (see use-rfq-lists); they're excluded from Order History below.
  AlgoType.ALGO_TYPE_RFQ,
  // Retain terminal cross-exchange arb states so a FAILED/COMPLETED arb with a non-flat
  // residual position (see features/arb/lib/arb-residual.ts) still has state to render the
  // persistent residual badge against, and so the History tab can show a Past section.
  AlgoType.ALGO_TYPE_CROSS_EXCHANGE_ARB,
  // Retain terminal spreader states for the same reason as arb above — a FAILED/COMPLETED/
  // CANCELLED spreader's unhedged taking-leg residual (see
  // features/spreader/lib/spreader-residual.ts) needs the terminal SpreaderState to render
  // against, and the History tab needs it for the Past section.
  AlgoType.ALGO_TYPE_SPREADER,
  // Retain terminal Advanced TWAP states — even though its terminal runs also convert into
  // an order-shaped row in Order History (see convertAlgosToHistoryItems below), the raw
  // AlgoState itself is kept here too so anything still reading algoStatesRawAtom directly
  // (e.g. a band/progress detail view) has the terminal AdvancedTwapState to render against.
  AlgoType.ALGO_TYPE_ADVANCED_TWAP,
  // A single hedge is one-shot: it is only ever Active for the few seconds it runs, so its
  // terminal state (and a failed/skipped leg's reason) must survive to reach History.
  AlgoType.ALGO_TYPE_SINGLE_HEDGE,
  // A stopped (Cancelled) or risk-halted (Failed) options MM keeps its final PnL, residual
  // greeks and config history on the running panel, and the list's "show ended" needs it.
  AlgoType.ALGO_TYPE_OPTIONS_MM,
])

// ============================================
// Composite key helpers
// ============================================

// Callers are expected to have already account-tagged incoming data (see the
// account-tagging step in account-orders-sync.tsx / account-positions-sync.tsx)
// before these ever run, so `accountId` is assumed non-empty here.
export function getOrderCompositeKey(order: Pick<Order, 'accountId' | 'orderId'>): string {
  return `${order.accountId}:${order.orderId}`
}

/** Apply a successful modify response immediately while the authoritative WS delta is in flight. */
export function patchOpenOrder(
  items: UnifiedOrderItem[],
  identity: Pick<Order, 'accountId' | 'orderId'>,
  changes: Partial<Pick<Order, 'price' | 'quantity'>>
): UnifiedOrderItem[] {
  const key = getOrderCompositeKey(identity)
  let changed = false
  const next = items.map((item) => {
    const order = item.order
    if (!order || getOrderCompositeKey(order) !== key) return item
    const price = changes.price ?? order.price
    const quantity = changes.quantity ?? order.quantity
    if (order.price === price && order.quantity === quantity) return item
    changed = true
    return { ...item, order: { ...order, price, quantity } }
  })
  return changed ? next : items
}

/** Remove a successfully cancelled order without waiting for a follow-up WS frame. */
export function removeOpenOrder(
  items: UnifiedOrderItem[],
  identity: Pick<Order, 'accountId' | 'orderId'>
): UnifiedOrderItem[] {
  const key = getOrderCompositeKey(identity)
  const next = items.filter((item) => !item.order || getOrderCompositeKey(item.order) !== key)
  return next.length === items.length ? items : next
}

/**
 * Rejected orders in `items` that `seen` has not reported yet, in arrival order.
 *
 * The orders stream re-sends an order on every subsequent frame, so a refusal
 * would otherwise re-notify forever. `seen` is MUTATED with the keys returned so
 * the caller's ref stays authoritative across frames.
 *
 * Callers must skip an account's initial dump: that replays historical orders,
 * and treating those as new would fire a wall of stale rejections on every page
 * load and reconnect.
 */
/**
 * A rejection older than this is treated as replayed history, not a live
 * refusal, when it arrives on an account's initial dump.
 */
export const REJECTION_FRESH_MS = 60_000

/**
 * Whether a newly-seen rejection should be reported to the user.
 *
 * The initial dump replays historical orders, so those must stay silent -
 * otherwise every page load and reconnect fires a wall of stale rejections.
 * But an account with NO open orders receives no dump message at all, so the
 * first genuine order to arrive is misclassified as the dump. That is exactly
 * when a user's first rejection lands (verified 2026-08-21: the first rejection
 * after a reload was swallowed, the second toasted). So a dump-classified
 * rejection is still reported when it is demonstrably fresh.
 *
 * Ordinary live rejections never consult the clock, so browser/server skew can
 * only cost the dump-edge toast - never the common path.
 */
export function shouldReportRejection(
  order: Pick<Order, 'createdAt' | 'updatedAt'>,
  isInitialDump: boolean,
  now: number
): boolean {
  if (!isInitialDump) return true
  const ts = orderEventTimestamp(order)
  if (ts === null) return false
  return now - ts <= REJECTION_FRESH_MS
}

/**
 * Event time of an order in epoch ms, preferring `updatedAt`; `null` when the
 * venue supplied neither.
 *
 * These are int64-as-string fields whose proto default is the STRING `'0'`,
 * which is truthy - so `updatedAt || createdAt` silently never falls back and
 * yields 0. Both timestamps must be checked numerically instead.
 */
export function orderEventTimestamp(order: Pick<Order, 'createdAt' | 'updatedAt'>): number | null {
  for (const raw of [order.updatedAt, order.createdAt]) {
    const n = Number(raw)
    if (Number.isFinite(n) && n > 0) return n
  }
  return null
}

export function selectNewlyRejectedOrders(items: UnifiedOrderItem[], seen: Set<string>): Order[] {
  const out: Order[] = []
  for (const item of items) {
    const order = item.order
    if (!order || order.status !== OrderStatus.ORDER_STATUS_REJECTED) continue
    const key = getOrderCompositeKey(order)
    if (seen.has(key)) continue
    seen.add(key)
    out.push(order)
  }
  return out
}

export function getAlgoCompositeKey(algo: Pick<AlgoState, 'accountIds' | 'id'>): string {
  return `${algo.accountIds[0] ?? ''}:${algo.id}`
}

function matchesAlgoIdentity(
  algo: Pick<AlgoState, 'accountIds' | 'id'>,
  identity: { accountId: string; algoId: string }
): boolean {
  return (
    algo.id === identity.algoId &&
    (algo.accountIds.length === 0 || algo.accountIds.includes(identity.accountId))
  )
}

/**
 * Reconcile a terminal algo response immediately. Types with an in-app Past section
 * retain the terminal state; ordinary TWAP/Limit Chase rows are removed.
 */
export function reconcileTerminalAlgoState(
  states: AlgoState[],
  identity: { accountId: string; algoId: string },
  status: AlgoStatus
): AlgoState[] {
  const target = states.find((state) => matchesAlgoIdentity(state, identity))
  if (!target) return states
  if (!RETAIN_TERMINAL_ALGO_TYPES.has(target.algoType)) {
    return states.filter((state) => !matchesAlgoIdentity(state, identity))
  }
  if (target.status === status) return states
  return states.map((state) =>
    matchesAlgoIdentity(state, identity) ? { ...state, status } : state
  )
}

export function getUnifiedOrderItemKey(item: UnifiedOrderItem): string {
  if (item.order?.orderId) return getOrderCompositeKey(item.order)
  if (item.algoState) return getAlgoCompositeKey(item.algoState)
  return ''
}

export function getTradeHistoryItemKey(item: TradeHistoryItem): string {
  const order = item.order
  if (order && (order.venueSymbol || order.orderId)) {
    return `${order.accountId}:${order.venueSymbol || order.orderId}`
  }
  if (item.algoState) return getAlgoCompositeKey(item.algoState)
  return ''
}

// ============================================
// Order / trade / algo merging
// ============================================

/**
 * Merges all order updates into existing order state for the Orders tab.
 * Keeps filled/cancelled/rejected orders instead of filtering to only open orders.
 * Sorted by updatedAt descending (newest first).
 */
export function mergeAllOrders(
  prev: UnifiedOrderItem[],
  updates: UnifiedOrderItem[]
): UnifiedOrderItem[] {
  const byKey = new Map<string, UnifiedOrderItem>()

  // Seed directly from `prev` — same item references, not rewrapped. Only the
  // `updates.forEach(upsert)` pass below should ever produce a new object, and
  // only for the specific keys an update actually touches. Previously this ran
  // BOTH `prev` and `updates` through `upsert`, which — since `byKey` starts
  // empty — took the "new" branch for every single existing order on every
  // single merge call, spreading it into a brand-new object regardless of
  // whether anything about it changed. That churned openOrdersRawAtom's every
  // element on every WS push, defeating reference-equality memoization for
  // every consumer (any `useMemo`/`selectAtom` keyed on these items always saw
  // a "changed" input even when nothing relevant to it moved).
  for (const item of prev) {
    const order = item.order
    if (!order?.orderId) continue
    byKey.set(getOrderCompositeKey(order), item)
  }

  const upsert = (item: UnifiedOrderItem) => {
    const order = item.order
    const orderId = order?.orderId
    if (!order || !orderId) return

    const key = getOrderCompositeKey(order)
    const existing = byKey.get(key)
    if (!existing) {
      byKey.set(key, {
        ...item,
        order: {
          ...order,
          orderId,
        },
      })
      return
    }

    // Order stream deltas can arrive out of order (e.g. a stale re-broadcast landing
    // after a modify's own fresher delta already applied). Applying an older snapshot
    // on top of a newer one would silently revert fields like price/quantity right
    // after the user edits them, since this upsert otherwise always takes the latest
    // message as truth regardless of arrival order. Only compare when both sides have
    // a parseable timestamp — an unparseable/missing one shouldn't block real updates.
    const existingTime = Number(existing.order?.updatedAt || existing.order?.createdAt || 0)
    const incomingTime = Number(order.updatedAt || order.createdAt || 0)
    if (existingTime > 0 && incomingTime > 0 && incomingTime < existingTime) {
      return
    }

    const cleanOrder = Object.fromEntries(
      Object.entries(order).filter(([k, v]) => {
        if (v === '' || v === undefined) return false
        if (k === 'side' && v === OrderSide.ORDER_SIDE_UNSPECIFIED) return false
        if (k === 'status' && v === OrderStatus.ORDER_STATUS_UNSPECIFIED) return false
        if (k === 'orderType' && v === OrderType.ORDER_TYPE_UNSPECIFIED) return false
        return true
      })
    ) as Order

    byKey.set(key, {
      ...existing,
      ...item,
      order: {
        ...(existing.order || {}),
        ...cleanOrder,
        orderId,
      } as Order,
    })
  }

  updates.forEach(upsert)

  const merged = Array.from(byKey.values())

  merged.sort((a, b) => {
    const aTime = Number(a.order?.updatedAt || a.order?.createdAt || 0)
    const bTime = Number(b.order?.updatedAt || b.order?.createdAt || 0)
    return bTime - aTime
  })

  return merged
}

/**
 * Merges trade updates (closed orders).
 * Sorted by updatedAt descending (newest first).
 */
export function mergeTrades(
  prev: TradeHistoryItem[],
  updates: TradeHistoryItem[]
): TradeHistoryItem[] {
  const result = [...prev]

  updates.forEach((newItem) => {
    const newOrder = newItem.order
    if (!newOrder) return

    const newKey = getTradeHistoryItemKey(newItem)
    const index = result.findIndex((item) => getTradeHistoryItemKey(item) === newKey)

    if (index !== -1) {
      const cleanNewOrder = Object.fromEntries(
        Object.entries(newOrder).filter(([k, v]) => {
          if (v === '' || v === undefined) return false
          if (k === 'side' && v === OrderSide.ORDER_SIDE_UNSPECIFIED) return false
          if (k === 'status' && v === OrderStatus.ORDER_STATUS_UNSPECIFIED) return false
          if (k === 'orderType' && v === OrderType.ORDER_TYPE_UNSPECIFIED) return false
          return true
        })
      ) as Order
      result[index] = {
        ...result[index],
        ...newItem,
        order: { ...(result[index].order || {}), ...cleanNewOrder } as Order,
      }
    } else {
      result.push(newItem)
    }
  })

  result.sort((a, b) => {
    const aTime = Number(a.order?.updatedAt || a.order?.createdAt || 0)
    const bTime = Number(b.order?.updatedAt || b.order?.createdAt || 0)
    return bTime - aTime
  })

  return result
}

/**
 * Returns true if an algo has been fully executed even if the server
 * hasn't sent a terminal status yet (e.g. still IN_PROGRESS).
 */
export function isAlgoFunctionallyComplete(algo: AlgoState): boolean {
  const twap = algo.algoData?.twap
  const limitChase = algo.algoData?.limitChase
  const scale = algo.algoData?.scale

  if (twap) {
    const total = Number(twap.totalQuantity || '0')
    return total > 0 && twap.remainingQuantity === '0'
  }
  if (limitChase) {
    const total = Number(limitChase.totalQuantity || '0')
    const filled = Number(limitChase.filledQuantity || '0')
    return total > 0 && filled >= total
  }
  if (scale) {
    // Every level filled. Not executed >= params.quantity: the planner floors each level
    // to the lot step, so the levels can sum to less than the total and never reach it.
    // A cancelled or rejected level is not "executed" — the server's terminal status
    // (COMPLETED / CANCELLED / FAILED) decides those rows.
    return (
      scale.levels.length > 0 &&
      scale.levels.every((level) => level.status === ScaleLevelStatus.SCALE_LEVEL_STATUS_FILLED)
    )
  }
  return false
}

/**
 * Merges algo state updates into existing algo states.
 * Sorted by createdAt descending (newest first).
 */
export function mergeAlgoStates(prev: AlgoState[], updates: AlgoState[]): AlgoState[] {
  const result = [...prev]

  updates.forEach((newState) => {
    const newKey = getAlgoCompositeKey(newState)
    const index = result.findIndex((s) => getAlgoCompositeKey(s) === newKey)
    if (index !== -1) {
      result[index] = {
        ...result[index],
        ...Object.fromEntries(
          Object.entries(newState).filter(([k, v]) => {
            // Drop only fields proto3 would default to when omitted on the wire
            // for unset string fields. `status: 0` is a valid COMPLETED transition
            // and must NOT be filtered (ALGO_STATUS_COMPLETED === 0).
            if (v === '' || v === undefined) return false
            // algoType is immutable once assigned; a partial delta that omits it
            // decodes to 0 (UNSPECIFIED) and would clobber a real type (e.g.
            // TRIGGER), breaking the Triggers-tab retention below.
            if (k === 'algoType' && v === AlgoType.ALGO_TYPE_UNSPECIFIED) return false
            return true
          })
        ),
      }
    } else {
      result.push(newState)
    }
  })

  const filtered = result
    .filter((s) => {
      // Keep trigger, hedge and RFQ algos in any state so their dedicated tabs can show
      // the full lifecycle (e.g. Pending → Firing → Fired triggers, cancelled hedges,
      // completed auto-RFQs) in a Past / history section.
      if (RETAIN_TERMINAL_ALGO_TYPES.has(s.algoType)) return true
      const keep =
        !TERMINAL_ALGO_STATUSES.has(s.status as AlgoStatus) && !isAlgoFunctionallyComplete(s)

      return keep
    })
    .sort((a, b) => Number(b.createdAt) - Number(a.createdAt))

  // Bound growth: keep every active algo, but cap retained terminal (fired /
  // cancelled / failed) algos to the most recent N per type, per account, so a
  // long session (or a second account) can't accumulate them without limit.
  // `filtered` is already sorted newest-first, so the oldest terminals fall off
  // first within each (accountId, algoType) group.
  const terminalByAccountAndType = new Map<string, number>()
  return filtered.filter((s) => {
    const isRetainedTerminal =
      RETAIN_TERMINAL_ALGO_TYPES.has(s.algoType) &&
      TERMINAL_ALGO_STATUSES.has(s.status as AlgoStatus)
    if (!isRetainedTerminal) return true
    const groupKey = `${s.accountIds[0] ?? ''}:${s.algoType}`
    const count = (terminalByAccountAndType.get(groupKey) ?? 0) + 1
    terminalByAccountAndType.set(groupKey, count)
    return count <= MAX_TERMINAL_ALGOS
  })
}

/**
 * Bounds a sorted (newest-first) list of order-history or trade-history items by
 * capping each account's own contribution to `maxPerAccount`, instead of capping
 * the merged total — otherwise one noisy/active account can push another
 * account's history out of the retained window. Groups by each item's own
 * `accountId` (order or algoState), not by a caller-supplied active-account list,
 * so it can't drift out of sync with whatever the caller thinks is "active".
 *
 * Known limitation: this only caps rows it actually received — it has no way
 * to detect or recover an account the server silently omitted from a batched
 * response entirely (no `has_more`/completeness signal exists in the
 * multi-account history contract).
 */
export function capHistoryPerAccount<T extends UnifiedOrderItem>(
  items: T[],
  maxPerAccount: number = MAX_HISTORY_SIZE
): T[] {
  const byAccount = new Map<string, T[]>()
  for (const item of items) {
    const accountId = item.order?.accountId || item.algoState?.accountIds?.[0] || ''
    const list = byAccount.get(accountId)
    if (list) {
      list.push(item)
    } else {
      byAccount.set(accountId, [item])
    }
  }

  const capped: T[] = []
  for (const list of byAccount.values()) {
    capped.push(...list.slice(0, maxPerAccount))
  }

  capped.sort((a, b) => {
    const aTime = Number(a.order?.updatedAt || a.order?.createdAt || a.algoState?.createdAt || 0)
    const bTime = Number(b.order?.updatedAt || b.order?.createdAt || b.algoState?.createdAt || 0)
    return bTime - aTime
  })

  return capped
}

/**
 * Drops items belonging to accounts that dropped out of scope (deactivated,
 * removed, or filtered out by the caller). Used by the batched-scope
 * coordinators to prune openOrdersRawAtom/orderHistoryRawAtom/tradesRawAtom
 * when the active-account list shrinks, replacing what used to be automatic
 * unmount-based cleanup back when each account had its own component instance.
 *
 * NOT for algoStatesRawAtom — that atom holds `AlgoState[]` directly, not
 * `UnifiedOrderItem[]`, so `item.algoState` is always undefined on it and this
 * would silently prune nothing. Use `pruneAlgoStates` for that atom instead.
 */
export function pruneUnifiedItems<T extends UnifiedOrderItem>(
  items: T[],
  droppedAccountIds: ReadonlySet<string>
): T[] {
  return items.filter((item) => {
    if (item.order?.accountId) return !droppedAccountIds.has(item.order.accountId)
    if (item.algoState) return !item.algoState.accountIds.some((id) => droppedAccountIds.has(id))
    return true
  })
}

/** `algoStatesRawAtom`-specific counterpart to `pruneUnifiedItems` — see there. */
export function pruneAlgoStates(
  states: AlgoState[],
  droppedAccountIds: ReadonlySet<string>
): AlgoState[] {
  return states.filter((state) => !state.accountIds.some((id) => droppedAccountIds.has(id)))
}

/** Same account-resolution rule `capHistoryPerAccount` uses — kept in sync deliberately. */
function resolveAccountId(item: UnifiedOrderItem): string {
  return item.order?.accountId || item.algoState?.accountIds?.[0] || ''
}

/**
 * Merges one REST history page into an atom's existing rows, per the
 * account_ids migration's first-page policy: a batched coordinator's *first*
 * page REPLACES that scope's existing rows (they may be stale — a previous
 * filter, a previous scope) rather than merging on top of them, preserves
 * rows for any account outside the current scope untouched, then layers in
 * the fresh seed, then any WS deltas that were buffered while REST was still
 * in flight (last, so a WS delta always wins over the REST seed for the same
 * row). Later pages just append/merge — no replace, no buffer, since only the
 * first page can race against buffered WS traffic.
 *
 * Extracted specifically so this policy — the highest-risk logic in the
 * batched-scope coordinators (`AccountOrdersCoordinator`/
 * `AccountTradesCoordinator` in account-orders-sync.tsx) — is unit-testable
 * without a React/React Query harness.
 */
export function mergeHistoryPage<T extends UnifiedOrderItem>(
  prev: T[],
  seedItems: T[],
  mergeFn: (prev: T[], updates: T[]) => T[],
  firstPage?: { scopeAccountIds: ReadonlySet<string>; bufferedItems: T[] }
): T[] {
  if (!firstPage) return mergeFn(prev, seedItems)
  const base = prev.filter((item) => !firstPage.scopeAccountIds.has(resolveAccountId(item)))
  const withSeed = mergeFn(base, seedItems)
  return mergeFn(withSeed, firstPage.bufferedItems)
}

const OPEN_ORDER_STATUSES = new Set([
  OrderStatus.ORDER_STATUS_UNSPECIFIED,
  OrderStatus.ORDER_STATUS_PENDING,
  OrderStatus.ORDER_STATUS_OPEN,
  OrderStatus.ORDER_STATUS_PARTIALLY_FILLED,
])

/** Whether an item's order is still in an "open" status, i.e. belongs in openOrdersRawAtom. */
export function isOpenOrderItem(item: UnifiedOrderItem): boolean {
  const status = item.order?.status
  return status !== undefined && OPEN_ORDER_STATUSES.has(status)
}

// ============================================
// Algo → order-history item conversion
// ============================================

export function convertAlgosToHistoryItems(algoItems: UnifiedOrderItem[]): UnifiedOrderItem[] {
  return (
    algoItems
      // Allowlist, not a blocklist: this function only knows how to derive order-shaped data
      // from twap/limitChase/advancedTwap/singleHedge/scale (below) — every other algo type (RFQ, Hedge, Trigger,
      // Arb, and any future type) would otherwise produce a blank-symbol, zero-quantity row.
      // Those types all have their own dedicated section (RFQs tab, Hedges/Triggers/Arb rows),
      // so excluding them here is the root-cause fix rather than relying solely on each row
      // hook's downstream filter to hide the resulting blank row.
      .filter(
        (item) =>
          item.algoState &&
          (item.algoState.algoType === AlgoType.ALGO_TYPE_TWAP ||
            item.algoState.algoType === AlgoType.ALGO_TYPE_LIMIT_CHASE ||
            item.algoState.algoType === AlgoType.ALGO_TYPE_ADVANCED_TWAP ||
            item.algoState.algoType === AlgoType.ALGO_TYPE_SINGLE_HEDGE ||
            item.algoState.algoType === AlgoType.ALGO_TYPE_SCALE)
      )
      .map((item) => {
        const algo = item.algoState!
        const twap = algo.algoData?.twap
        const limitChase = algo.algoData?.limitChase
        const advancedTwap = algo.algoData?.advancedTwap
        const singleHedge = algo.algoData?.singleHedge
        // The row shows the first leg: a single hedge scoped to one underlying (the dialog's
        // default and the trigger action's only shape) has exactly one. Before the leg is
        // sized, the params' instrument keeps the row from rendering blank.
        const hedgeLeg = singleHedge?.legs[0]
        const hedgeParams = singleHedge?.params
        const scale = algo.algoData?.scale
        const scaleParams = scale?.params

        const symbol =
          twap?.symbol ??
          limitChase?.symbol ??
          advancedTwap?.symbol ??
          (hedgeLeg?.hedgeInstrument || hedgeParams?.hedgeInstrument) ??
          scaleParams?.symbol ??
          ''
        const side =
          twap?.direction ??
          limitChase?.direction ??
          advancedTwap?.direction ??
          hedgeLeg?.side ??
          scaleParams?.orderSide ??
          OrderSide.ORDER_SIDE_UNSPECIFIED
        const totalQty =
          twap?.totalQuantity ??
          limitChase?.totalQuantity ??
          advancedTwap?.totalQuantity ??
          hedgeLeg?.quantity ??
          scaleParams?.quantity ??
          '0'
        const filledQty = twap
          ? String(
              Math.max(0, Number(twap.totalQuantity || '0') - Number(twap.remainingQuantity || '0'))
            )
          : advancedTwap
            ? (advancedTwap.executedQuantity ?? '0')
            : singleHedge
              ? // Only an inline MARKET leg reports `filled`; a TWAP/limit-chase leg's fills
                // live on its child algo.
                hedgeLeg?.filled || '0'
              : scaleParams
                ? scale?.executedQuantity || '0'
                : (limitChase?.filledQuantity ?? '0')

        const isFunctionallyComplete = isAlgoFunctionallyComplete(algo)
        // FAILED maps to REJECTED, not CANCELLED: an algo fails because the venue
        // refused its slice (margin, post-only would take, ...), which is what
        // REJECTED already means for a direct order — and it's the status direct
        // orders carry `rejectReason` on, so the history table renders both the
        // same way. Collapsing it into CANCELLED made a margin-failed TWAP look like
        // one the user stopped themselves, reported as "orders missing".
        const isFailed = algo.status === AlgoStatus.ALGO_STATUS_FAILED && !isFunctionallyComplete
        const status =
          algo.status === AlgoStatus.ALGO_STATUS_COMPLETED || isFunctionallyComplete
            ? OrderStatus.ORDER_STATUS_FILLED
            : isFailed
              ? OrderStatus.ORDER_STATUS_REJECTED
              : TERMINAL_ALGO_STATUSES.has(algo.status)
                ? OrderStatus.ORDER_STATUS_CANCELLED
                : OrderStatus.ORDER_STATUS_OPEN

        // Derive instrumentKind from symbol
        let instrumentKind = InstrumentKind.INSTRUMENT_KIND_UNSPECIFIED
        if (symbol.includes('PERPETUAL') || symbol.includes('PERP')) {
          instrumentKind = InstrumentKind.INSTRUMENT_KIND_FUTURE
        } else if (/-[CP](-|$)/.test(symbol) || symbol.split('-').length >= 3) {
          instrumentKind = InstrumentKind.INSTRUMENT_KIND_OPTION
        } else if (symbol.includes('-') && !symbol.includes('_')) {
          instrumentKind = InstrumentKind.INSTRUMENT_KIND_FUTURE // Likely a future
        } else if (symbol.includes('_') || (symbol && !symbol.includes('-'))) {
          instrumentKind = InstrumentKind.INSTRUMENT_KIND_SPOT
        }

        // Derive exchange. TwapState carries its own `exchange` just like
        // LimitChaseState does — reading only the limit-chase one sent every
        // TWAP row through the symbol heuristic below, which labels any
        // hyphenated symbol Deribit (so a Lighter/HL `ETH-PERP` TWAP showed a
        // Deribit icon). The heuristic stays as the last resort for algo
        // states old enough to predate the field.
        const tradeVenue =
          limitChase?.tradeVenue ??
          twap?.tradeVenue ??
          advancedTwap?.tradeVenue ??
          hedgeParams?.tradeVenue ??
          scaleParams?.tradeVenue ??
          0
        const exchangeValue =
          twap?.exchange ??
          limitChase?.exchange ??
          advancedTwap?.exchange ??
          (hedgeParams?.exchange || undefined) ??
          (scaleParams?.exchange || undefined) ??
          (tradeVenue !== TradeVenue.TRADE_VENUE_UNSPECIFIED
            ? toExchangeEnum(tradeVenueToExchangeKey(tradeVenue))
            : symbol.includes('-')
              ? Exchange.EXCHANGE_DERIBIT
              : Exchange.EXCHANGE_BINANCE)

        return {
          ...item,
          order: {
            orderId: algo.id,
            // Guaranteed non-empty by the ingest-time account-tagging step (Step 1)
            // that runs before this conversion — algo.accountIds is tagged with the
            // owning subscription's accountId if the server didn't stamp it.
            accountId: algo.accountIds[0] || '',
            symbol,
            side,
            quantity: totalQty,
            filledQuantity: filledQty,
            // The algo's own quantity unit — totalQty/filledQty above are in it.
            denomination:
              twap?.denomination ??
              limitChase?.denomination ??
              advancedTwap?.denomination ??
              // SingleHedgeLeg.quantity is venue base units.
              (singleHedge ? Denomination.DENOMINATION_BASE : undefined) ??
              scaleParams?.denomination ??
              Denomination.DENOMINATION_UNSPECIFIED,
            status,
            // Only on FAILED: `failureReason` is the *latest* venue reject, which
            // on a still-running algo may describe an attempt it since retried past.
            rejectReason: isFailed ? algo.failureReason : undefined,
            updatedAt: algo.updatedAt,
            createdAt: algo.createdAt,
            label: 'algo',
            // TwapState has no `activePrice`, so every TWAP fell through to
            // '0' and — now that running TWAPs reach the Active tab — rendered
            // as "10 @ 0", indistinguishable from a genuinely zero-priced
            // order. `limitPrice` mirrors TwapAlgoParams.limit_price.
            // AdvancedTwapState carries neither — `arrivalPrice` (the band's
            // anchor, captured once at run start) is the closest reference
            // price it has, same "reference, not true avg fill" caveat as
            // TWAP's limitPrice above.
            // A Scale's levels each fill at their own limit, so its base-weighted level price
            // is a true average; before any fill, `referencePrice` (the mid the ladder was
            // laid against) stands in, with the same caveat as arrivalPrice above.
            avgFillPrice:
              limitChase?.activePrice ??
              twap?.limitPrice ??
              advancedTwap?.arrivalPrice ??
              (scale
                ? (scaleAvgFillPrice(
                    scale.levels,
                    scaleParams?.denomination ?? Denomination.DENOMINATION_BASE
                  ) ??
                  (scale.referencePrice || '0'))
                : undefined) ??
              '0',
            exchange: exchangeValue,
            tradeVenue,
            venueSymbol: '',
            orderType: algo.algoType as unknown as OrderType,
            instrumentKind,
            isLiquidation: false,
            isRebalance: false,
            timeInForce: '',
            reduceOnly: scaleParams?.reduceOnly ?? false,
          },
        }
      })
  )
}

// ============================================
// Position side-effects
// ============================================

/**
 * Applies filled closing orders to positions.
 * SELL order reduces/removes a long (BUY-side) position; BUY order reduces/removes a short.
 * Called only for delta WS updates to bridge the gap before the server sends a position delta.
 *
 * Account- and exchange-scoped: an order can only patch a position that matches
 * both its accountId and exchange, since two different accounts (or the same
 * account on two exchanges) can hold a position in the same symbol. This is only
 * safe because ingest-time tagging (Step 1) guarantees `order.accountId` and
 * `order.exchange` are always populated by the time this runs.
 */
export function applyFilledOrdersToPositions(
  positions: Position[],
  filledItems: UnifiedOrderItem[]
): Position[] {
  let result = [...positions]

  filledItems.forEach((item) => {
    const order = item.order!
    const filledQty = safeParseFloat(order.filledQuantity || order.quantity || '0')
    if (filledQty <= 0) return

    // Only the row this fill closes may be dropped. Filtering the whole list (as this once
    // did) removed every short across every account on any fill — venues like Deribit
    // report a short's quantity as negative — and deltas never re-add an untouched row,
    // so they stayed gone until the next full snapshot (a hard refresh).
    result = result.flatMap((pos) => {
      if (pos.symbol !== order.symbol) return [pos]
      if (pos.accountId !== order.accountId) return [pos]
      if (pos.exchange !== order.exchange) return [pos]

      // reduceOnly orders always close a position regardless of direction
      // For non-reduceOnly, skip positions with unspecified side (server delta handles it)
      if (pos.side === OrderSide.ORDER_SIDE_UNSPECIFIED && !order.reduceOnly) return [pos]

      // Closing direction: SELL closes LONG (BUY side), BUY closes SHORT (SELL side)
      const isClosing =
        order.reduceOnly === true ||
        (order.side === OrderSide.ORDER_SIDE_SELL && pos.side === OrderSide.ORDER_SIDE_BUY) ||
        (order.side === OrderSide.ORDER_SIDE_BUY && pos.side === OrderSide.ORDER_SIDE_SELL)

      if (!isClosing) return [pos]

      // Reduce the size's magnitude and keep its sign (a short may be stored negative).
      const posQty = safeParseFloat(pos.quantity || '0')
      const remaining = Math.max(0, Math.abs(posQty) - filledQty)
      if (remaining === 0) return []
      return [{ ...pos, quantity: String(posQty < 0 ? -remaining : remaining) }]
    })
  })

  return result
}
